import { Component, effect, input, output, signal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { isNil } from '@gfe/core/utils/shared-utils';

import { FormGroupOptional } from '../form-builder/form-builder.model';
import { FormMode } from '../row-edition-form.utils';

@Component({
  imports: [TranslatePipe],
  selector: 'app-form-switch',
  templateUrl: './form-switch.component.html'
})
export class FormSwitchComponent {
  form = input.required<FormGroup>();
  formMode = input.required<FormMode>();
  isFormEnable = signal<boolean>(true);
  isFormRequired = signal<boolean>(false);

  private isFirstTime = true;

  selectionChanged = output<boolean>();
  constructor() {
    effect(() => {
      const form = this.form();
      const formMode = this.formMode();

      const isRequired = (form as FormGroupOptional).isRequired;
      this.isFormRequired.set(isRequired);

      if (formMode === 'edit') {
        this.isFormEnable.set(this.form().enabled);
        return;
      }

      if (formMode === 'create') {
        if (!isNil(form.parent) && !isRequired) {
          this.isFormEnable.set(false);
        }
      } else {
        this.isFormEnable.set(this.form().enabled);
      }
    });

    effect(() => {
      const isEnable = this.isFormEnable();
      if (this.isFirstExecution()) {
        this.selectionChanged.emit(isEnable);
        return;
      }
      if (isEnable) {
        this.form().enable();
      } else {
        this.form().disable();
      }
      this.selectionChanged.emit(isEnable);
    });
  }

  onInput() {
    this.isFormEnable.set(!this.isFormEnable());
  }

  private isFirstExecution(): boolean {
    if (this.isFirstTime) {
      this.isFirstTime = false;
      return true;
    }
    return false;
  }

  private formGroupHasAnyValue(control: AbstractControl): boolean {
    if (control instanceof FormControl) {
      const value = control.value;
      return (
        (value !== null && value !== undefined && value !== '') ||
        (typeof value === 'string' && value.trim() !== '')
      );
    }
    if (control instanceof FormGroup) {
      return Object.values(control.controls).some((c) => this.formGroupHasAnyValue(c));
    }
    if (control instanceof FormArray) {
      return control.controls.some((c) => this.formGroupHasAnyValue(c));
    }
    return false;
  }

  private enableGroupButKeepDisabledChildren(form: FormGroup, isRoot: boolean = true) {
    // First: enable the group itself
    if (isRoot || (form as FormGroupOptional).isRequired) {
      form.enable({ emitEvent: false, onlySelf: true });
    }
    // Then: re-disable any child controls that were originally disabled
    Object.keys(form.controls).forEach((key) => {
      const control = form.controls[key];

      // If the control is a group/array, recurse
      if (control instanceof FormGroup) {
        // Save disabled state before enabling the parent
        if (control.disabled) {
          control.disable();
        } else {
          this.enableGroupButKeepDisabledChildren(control, false);
        }
      }
    });
  }
}
